/**
 * `<ContactMemoriesTab>` — long-term memories remembered about a contact
 * (conversation-memory unit, PR 3.5). Lists contact-scoped + workspace-scoped
 * memories, shows the owning agent, and lets an operator edit a memory's
 * stage (temporary/permanent) and visibility (private/workspace) or delete it.
 *
 * Memories are written automatically after runs when the workspace
 * `memoryEnabled` kill switch is on (Settings → Workspace).
 */

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TableBody,
  TableHeader,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "../icons";

import { DataGrid, DataGridCell, DataGridHead, DataGridHeaderRow, DataGridRow } from "../data-grid";
import { StatusPill } from "../status-pill";
import { useContactsClient } from "../../lib/api/use-contacts-client";
import type { Memory } from "../../lib/api/types";

export interface ContactMemoriesTabProps {
  contactId: string;
}

type MemoryPatch = { stage?: Memory["stage"]; visibility?: Memory["visibility"] };

/** Render a structured variable value (string/number/boolean/date/json) as a
 *  short human-readable string for the memories table. */
function formatMemoryValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return JSON.stringify(value);
}

export function ContactMemoriesTab({ contactId }: ContactMemoriesTabProps) {
  const client = useContactsClient();
  const qc = useQueryClient();
  const queryKey = ["contacts", "memories", contactId] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => client.listMemories(contactId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: MemoryPatch }) =>
      client.updateMemory(id, patch),
    onSuccess: (updated) => {
      qc.setQueryData<Memory[]>(queryKey, (prev) =>
        prev ? prev.map((m) => (m.id === updated.id ? updated : m)) : prev,
      );
      toast.success("Memory updated");
    },
    onError: () => toast.error("Could not update memory"),
  });

  const deleteMutation = useMutation({
    mutationFn: (memoryId: string) => client.deleteMemory(memoryId),
    onSuccess: (_res, memoryId) => {
      qc.setQueryData<Memory[]>(queryKey, (prev) =>
        prev ? prev.filter((m) => m.id !== memoryId) : prev,
      );
      toast.success("Memory deleted");
    },
    onError: () => toast.error("Could not delete memory"),
  });

  if (query.isPending) {
    return (
      <div
        className="space-y-2 rounded-xl border border-border p-4"
        data-testid="memories-skeleton"
      >
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="rounded-xl border border-border p-4">
        <p className="text-sm text-destructive" data-testid="memories-error">
          Could not load memories.
        </p>
      </div>
    );
  }

  const memories = query.data ?? [];

  if (memories.length === 0) {
    return (
      <div className="rounded-xl border border-border p-4">
        <p className="text-sm text-muted-foreground" data-testid="memories-empty">
          No memories yet. Facts about this contact are remembered automatically after conversations
          once long-term memory is enabled in Settings → Workspace.
        </p>
      </div>
    );
  }

  return (
    <div className="-mx-5 overflow-x-auto border-y border-border">
      <DataGrid data-testid="memories-table" className="min-w-[64rem] border-b-0">
        <TableHeader>
          <DataGridHeaderRow>
            <DataGridHead className="w-44 pl-5">Variable</DataGridHead>
            <DataGridHead className="w-40">Key</DataGridHead>
            <DataGridHead className="w-32">Value</DataGridHead>
            <DataGridHead className="w-28">Kind</DataGridHead>
            <DataGridHead className="w-24">Scope</DataGridHead>
            <DataGridHead className="w-40">Stage</DataGridHead>
            <DataGridHead className="w-40">Visibility</DataGridHead>
            <DataGridHead className="w-28">Contact</DataGridHead>
            <DataGridHead className="w-28">ID</DataGridHead>
            <DataGridHead className="w-24 pr-5 text-right" divider={false}>
              Actions
            </DataGridHead>
          </DataGridHeaderRow>
        </TableHeader>
        <TableBody>
          {memories.map((m) => (
            <DataGridRow key={m.id} className="last:border-0" data-testid={`memory-row-${m.id}`}>
              <DataGridCell className="pl-5 text-foreground">
                {m.variableDisplayName ?? m.variableKey ?? (
                  <span className="text-muted-foreground">—</span>
                )}
              </DataGridCell>
              <DataGridCell className="font-mono text-xs">
                {m.variableKey ?? <span className="text-muted-foreground">—</span>}
              </DataGridCell>
              <DataGridCell className="text-foreground">{formatMemoryValue(m.value)}</DataGridCell>
              <DataGridCell>
                <StatusPill tone="neutral" className="capitalize">
                  {m.kind}
                </StatusPill>
              </DataGridCell>
              <DataGridCell className="text-xs">{m.scope}</DataGridCell>
              <DataGridCell>
                <Select
                  value={m.stage}
                  disabled={updateMutation.isPending}
                  onValueChange={(stage) =>
                    updateMutation.mutate({ id: m.id, patch: { stage: stage as Memory["stage"] } })
                  }
                >
                  <SelectTrigger className="h-7 text-xs" data-testid={`memory-stage-${m.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="temporary">Temporary</SelectItem>
                    <SelectItem value="permanent">Permanent</SelectItem>
                  </SelectContent>
                </Select>
              </DataGridCell>
              <DataGridCell>
                <Select
                  value={m.visibility}
                  disabled={updateMutation.isPending}
                  onValueChange={(visibility) =>
                    updateMutation.mutate({
                      id: m.id,
                      patch: { visibility: visibility as Memory["visibility"] },
                    })
                  }
                >
                  <SelectTrigger className="h-7 text-xs" data-testid={`memory-visibility-${m.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="workspace">Workspace</SelectItem>
                  </SelectContent>
                </Select>
              </DataGridCell>
              <DataGridCell className="font-mono text-xs" title={m.contactId ?? undefined}>
                {m.contactId ? m.contactId.slice(0, 8) : "—"}
              </DataGridCell>
              <DataGridCell className="font-mono text-xs" title={m.id}>
                {m.id.slice(0, 8)}
              </DataGridCell>
              <DataGridCell className="pr-5 text-right" divider={false}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete memory"
                  data-testid={`memory-delete-${m.id}`}
                  className="h-7 w-7"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(m.id)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </DataGridCell>
            </DataGridRow>
          ))}
        </TableBody>
      </DataGrid>
    </div>
  );
}
